import { MeshUtils } from 'src/app/platform/busines-data/Geometries/Utils/mesh-utils';
import { DomainEntity } from "./DomainEntity";
import * as THREE from '@phdsoft/three';
import { BufferGeometry, Object3D } from "@phdsoft/three";
import { mergeVertices } from 'three/examples/jsm/utils/BufferGeometryUtils.js';
import { EdgesGeometry } from './Geometries/Utils/edges-geometry';
import { isLightModelEdges } from 'src/app/digital-twin/Loader/light-model-edges';

export abstract class GeometricElement extends DomainEntity {
    public ShowAs3D: boolean;
    public SurfaceDesign: THREE.Mesh;
    public SurfaceBoundary: any;
    public LineDesign: THREE.Object3D;
    public Line: Array<number>;
    public LineNormals: Array<number>;

    private _elementNormal: THREE.Vector3;
    public get elementNormal(): THREE.Vector3 {
        return this._elementNormal;
    }
    public set elementNormal(v: THREE.Vector3) {
        this._elementNormal = v;
    }

    private _customMaterial: THREE.Material | null = null;
    public get customMaterial(): THREE.Material | null {
        return this._customMaterial;
    }
    public set customMaterial(v: THREE.Material | null) {
        this._customMaterial = v;
    }

    private _defaultMaterial: THREE.Material;
    public get defaultMaterial(): THREE.Material {
        return this._customMaterial ?? this._defaultMaterial;
    }
    public set defaultMaterial(v: THREE.Material) {
        if (this.SurfaceDesign)
            this.SurfaceDesign.material = v;
        this._currentMaterial = v;
        this._defaultMaterial = v;
    }

    private _currentMaterial: THREE.Material;
    public get currentMaterial(): THREE.Material {
        return this._currentMaterial;
    }
    public set currentMaterial(v: THREE.Material) {
        this._currentMaterial = v;
    }

    public set currentRenderMaterial(v: THREE.Material) {
        this._currentMaterial = v;
        this.SurfaceDesign.material = v;
    }

    private _executedMaterial: THREE.Material;
    public get executedMaterial(): THREE.Material {
        return this._executedMaterial;
    }
    public set executedMaterial(v: THREE.Material) {
        this._executedMaterial = v;
    }


    private _outlinedMaterial: THREE.Material;
    public get outlinedMaterial(): THREE.Material {
        return this._outlinedMaterial;
    }
    public set outlinedMaterial(v: THREE.Material) {
        this._outlinedMaterial = v;
    }


    public get visible(): boolean {
        return this.SurfaceDesign ? this.SurfaceDesign.visible : this.LineDesign.visible;
    }

    public get realVisibility(): boolean {
        return this.SurfaceDesign ? this.SurfaceDesign.visible && this.SurfaceDesign.parent.visible &&
            this.SurfaceDesign.parent.parent.visible && this.SurfaceDesign.parent.parent.parent.visible
            && this.SurfaceDesign.parent.parent.parent.visible : this.LineDesign.visible && this.LineDesign.parent.visible &&
            this.LineDesign.parent.parent.visible && this.LineDesign.parent.parent.parent.visible
        && this.LineDesign.parent.parent.parent.visible;
    }

    private _femElementIds: Array<number>;
    public get femElementIds(): Array<number> {
        return this._femElementIds;
    }
    public set femElementIds(v: Array<number>) {
        this._femElementIds = v;
    }

    private _femNodeIds: Array<number>;
    public get femNodeIds(): Array<number> {
        return this._femNodeIds;
    }
    public set femNodeIds(v: Array<number>) {
        this._femNodeIds = v;
    }

    private _femTopology: Array<number>;
    public get femTopology(): Array<number> {
        return this._femTopology;
    }
    public set femTopology(v: Array<number>) {
        this._femTopology = v;
    }

    
    private _Area : number;
    public get Area() : number {
        return this._Area;
    }
    public set Area(v : number) {
        this._Area = v;
    }
    
    
    private _Width : number;
    public get Width() : number {
        return this._Width;
    }
    public set Width(v : number) {
        this._Width = v;
    }
    
    
    private _Height : number;
    public get Height() : number {
        return this._Height;
    }
    public set Height(v : number) {
        this._Height = v;
    }
    

    constructor(mesh?: THREE.Mesh, material?: THREE.Material, id?: string) {
        super();
        // Only weld freshly drawn (non-indexed) geometry. Already-indexed geometry
        // (e.g. inspection areas rebuilt from saved ElementInfo.vertices/faces) must
        // keep its stored index: re-welding remaps it and corrupts old inspections.
        // FEM-linked geometry (ElementsIds/NodesIds/ElementTopology from the FEA
        // neutral-file link) must NOT be welded either: the FEA painters
        // (FeaResult.setColorByElement/ByNode/SmoothByElement) walk the vertices in
        // the conversor's order, 6 per quad / 3 per triangle, one NodesIds entry per
        // vertex. Welding collapses shared nodes and reorders vertices, so the colors
        // land on the wrong vertices (wavy gradients instead of flat panels). On prod
        // this weld never ran for GLB meshes (two vendored three.js instances made
        // the instanceof check fail); keep that behavior for FEM-linked elements.
        if (mesh?.geometry instanceof BufferGeometry && !mesh?.geometry?.index
            && !GeometricElement.hasFemLink(mesh))
            mesh.geometry = mergeVertices(mesh?.geometry as BufferGeometry);
        mesh?.geometry.computeVertexNormals();
        this.SurfaceBoundary = mesh?.userData.Boundaries;
        if (mesh) {
            this.SurfaceDesign = mesh;
            this.SurfaceDesign.name = id;
            this.SurfaceDesign.material = material;
            this.SurfaceDesign.userData.scale = 1;
            this._femElementIds = mesh.userData.ElementsIds;
            this._femTopology = mesh.userData.ElementTopology;
            this._femNodeIds = mesh.userData.NodesIds;
        }

        this.defaultMaterial = material;
        this.currentMaterial = material;
        this.Line = mesh?.userData.Line;

        this._elementNormal = new THREE.Vector3();
        if ((mesh?.geometry.type === 'BufferGeometry')) {
            const _normalArray = (mesh?.geometry as THREE.BufferGeometry).attributes.normal.array;
            for (let i = 0; i < _normalArray.length; i += 3) {
                const x = _normalArray[i],
                    y = _normalArray[i + 1],
                    z = _normalArray[i + 2];
                this._elementNormal.add(new THREE.Vector3(x, y, z));
            }
            this._elementNormal.normalize();
            (mesh?.geometry as THREE.BufferGeometry).deleteAttribute('color');
        }
        if(mesh)
            this.ComputeAttributes();
    }

    /** True when the mesh carries FEM link data whose order is tied to the vertex order. */
    private static hasFemLink(mesh: THREE.Mesh): boolean {
        const ud = mesh?.userData;
        return Array.isArray(ud?.ElementsIds) && ud.ElementsIds.length > 0
            || Array.isArray(ud?.NodesIds) && ud.NodesIds.length > 0;
    }

    /**
     * Sum of triangle areas of a NON-indexed geometry (3 consecutive vertices per
     * face). Indexed geometry is handled inline by the callers; unwelded
     * FEM-linked elements (see constructor) have no index and would otherwise
     * report an area of 0.
     */
    private static nonIndexedArea(geometry: THREE.BufferGeometry, scale: number = 1): number {
        const pos = geometry?.attributes?.position;
        if (!pos) return 0;
        let area = 0;
        const vA = new THREE.Vector3(), vB = new THREE.Vector3(), vC = new THREE.Vector3();
        for (let i = 0; i + 2 < pos.count; i += 3) {
            vA.fromBufferAttribute(pos, i).multiplyScalar(scale);
            vB.fromBufferAttribute(pos, i + 1).multiplyScalar(scale);
            vC.fromBufferAttribute(pos, i + 2).multiplyScalar(scale);
            area += new THREE.Triangle(vA, vB, vC).getArea();
        }
        return area;
    }

    public createBorder(material: THREE.Material): void {
        // Replace, don't stack: Section.renderBorders calls this again on every region select
        // (DTSelectionService.selectRegion -> Region.renderBorderofElements), so drop the previous
        // border lines first. Light-model crease edges and non-line children stay; the material is
        // shared (GraphicsMaterials.borderMaterialLineBasic) and is never disposed here.
        // PhantomSurfaceDesign has no children, so nothing is removed from it.
        for (const child of [...this.SurfaceDesign.children]) {
            if (!(child as THREE.Line).isLine || isLightModelEdges(child)) continue;
            this.SurfaceDesign.remove(child);
            (child as THREE.Line).geometry?.dispose();
        }
        if (this.SurfaceBoundary != null && this.SurfaceBoundary.length > 0) {
            for (let j = 0; j < this.SurfaceBoundary.length; j++) {
                const linePoints: number[] = [];
                for (let k = 0; k < this.SurfaceBoundary[j].length; k++) {
                    linePoints.push(this.SurfaceBoundary[j][k]);
                }
                if (linePoints.length === 0) {
                    continue;
                }
                const geom = new THREE.BufferGeometry();
                geom.setAttribute('position', new THREE.Float32BufferAttribute(linePoints, 3));
                const line = new THREE.LineLoop(geom, material);
                this.SurfaceDesign.add(line);
            }
        }
        else {
            const loops = this.computeBorderLoops();
            if (loops.length > 0) {
                for (const loop of loops) {
                    const geom = new THREE.BufferGeometry();
                    geom.setAttribute('position', new THREE.Float32BufferAttribute(loop, 3));
                    const line = new THREE.LineLoop(geom, material);
                    this.SurfaceDesign.add(line);
                }
            } else 
            {
                var edges = new EdgesGeometry(this.SurfaceDesign.geometry, 61);
                var cleanEdges = MeshUtils.GuaranteeEdgesAreOk(edges);
                const linearObject = new THREE.LineSegments(cleanEdges, material);
                this.SurfaceDesign.add(linearObject);
            }
        }
    }

    /**
     * Computes border loops for disconnected open meshes.
     * Boundary vertices are welded only across different connected components,
     * preserving curved borders while removing duplicated internal seams.
     */
    private computeBorderLoops(tol: number = 0): number[][] {
        const geometry = this.SurfaceDesign.geometry as THREE.BufferGeometry;
        const posAttr = geometry.attributes.position;
        if (!posAttr) return [];

        const V = posAttr.array as ArrayLike<number>;
        const vertCount = posAttr.count;
        if (vertCount === 0) return [];

        let F: number[];
        let faceCount: number;
        if (geometry.index) {
            F = Array.from(geometry.index.array as ArrayLike<number>);
            faceCount = F.length / 3;
        } else {
            faceCount = vertCount / 3;
            F = new Array(faceCount * 3);
            for (let i = 0; i < F.length; i++) F[i] = i;
        }
        if (faceCount === 0) return [];

        if (tol <= 0) {
            let minX = Infinity, minY = Infinity, minZ = Infinity;
            let maxX = -Infinity, maxY = -Infinity, maxZ = -Infinity;
            for (let i = 0; i < vertCount; i++) {
                const x = V[i * 3], y = V[i * 3 + 1], z = V[i * 3 + 2];
                if (x < minX) minX = x; if (x > maxX) maxX = x;
                if (y < minY) minY = y; if (y > maxY) maxY = y;
                if (z < minZ) minZ = z; if (z > maxZ) maxZ = z;
            }
            const dx = maxX - minX, dy = maxY - minY, dz = maxZ - minZ;
            const diag = Math.sqrt(dx * dx + dy * dy + dz * dz);
            tol = Math.max(1e-9, diag * 5e-5);
        }

        const edgeKey = (a: number, b: number): string => a < b ? `${a}_${b}` : `${b}_${a}`;

        // 1) Original connected components.
        const compParent = new Int32Array(vertCount);
        const compRank = new Int32Array(vertCount);
        for (let i = 0; i < vertCount; i++) compParent[i] = i;

        const compFind = (x: number): number => {
            while (compParent[x] !== x) {
                compParent[x] = compParent[compParent[x]];
                x = compParent[x];
            }
            return x;
        };
        const compUnion = (a: number, b: number): void => {
            a = compFind(a); b = compFind(b);
            if (a === b) return;
            if (compRank[a] < compRank[b]) { const t = a; a = b; b = t; }
            compParent[b] = a;
            if (compRank[a] === compRank[b]) compRank[a]++;
        };

        for (let f = 0; f < faceCount; f++) {
            const i0 = F[f * 3], i1 = F[f * 3 + 1], i2 = F[f * 3 + 2];
            if (i0 === i1 || i1 === i2 || i2 === i0) continue;
            compUnion(i0, i1);
            compUnion(i1, i2);
            compUnion(i2, i0);
        }

        // 2) Find original boundary edges/vertices.
        const originalEdgeUse = new Map<string, number>();
        for (let f = 0; f < faceCount; f++) {
            const i0 = F[f * 3], i1 = F[f * 3 + 1], i2 = F[f * 3 + 2];
            if (i0 === i1 || i1 === i2 || i2 === i0) continue;
            const e0 = edgeKey(i0, i1), e1 = edgeKey(i1, i2), e2 = edgeKey(i2, i0);
            originalEdgeUse.set(e0, (originalEdgeUse.get(e0) || 0) + 1);
            originalEdgeUse.set(e1, (originalEdgeUse.get(e1) || 0) + 1);
            originalEdgeUse.set(e2, (originalEdgeUse.get(e2) || 0) + 1);
        }

        const isBoundaryVert = new Uint8Array(vertCount);
        originalEdgeUse.forEach((count, key) => {
            if (count !== 1) return;
            const sep = key.indexOf('_');
            const a = parseInt(key.substring(0, sep), 10);
            const b = parseInt(key.substring(sep + 1), 10);
            isBoundaryVert[a] = 1;
            isBoundaryVert[b] = 1;
        });

        const boundaryVerts: number[] = [];
        for (let i = 0; i < vertCount; i++) {
            if (isBoundaryVert[i]) boundaryVerts.push(i);
        }
        if (boundaryVerts.length === 0) return [];

        // 3) Weld boundary vertices across different original components only.
        const weldParent = new Int32Array(vertCount);
        const weldRank = new Int32Array(vertCount);
        for (let i = 0; i < vertCount; i++) weldParent[i] = i;

        const weldFind = (x: number): number => {
            while (weldParent[x] !== x) {
                weldParent[x] = weldParent[weldParent[x]];
                x = weldParent[x];
            }
            return x;
        };
        const weldUnion = (a: number, b: number): void => {
            a = weldFind(a); b = weldFind(b);
            if (a === b) return;
            if (weldRank[a] < weldRank[b]) { const t = a; a = b; b = t; }
            weldParent[b] = a;
            if (weldRank[a] === weldRank[b]) weldRank[a]++;
        };

        const invTol = 1 / tol;
        const grid = new Map<string, number[]>();
        for (const i of boundaryVerts) {
            const cx = Math.floor(V[i * 3] * invTol);
            const cy = Math.floor(V[i * 3 + 1] * invTol);
            const cz = Math.floor(V[i * 3 + 2] * invTol);
            const key = `${cx},${cy},${cz}`;
            let list = grid.get(key);
            if (!list) { list = []; grid.set(key, list); }
            list.push(i);
        }

        const tolSq = tol * tol;
        for (const i of boundaryVerts) {
            const compI = compFind(i);
            const xi = V[i * 3], yi = V[i * 3 + 1], zi = V[i * 3 + 2];
            const cx = Math.floor(xi * invTol);
            const cy = Math.floor(yi * invTol);
            const cz = Math.floor(zi * invTol);

            for (let dx = -1; dx <= 1; dx++) {
                for (let dy = -1; dy <= 1; dy++) {
                    for (let dz = -1; dz <= 1; dz++) {
                        const key = `${cx + dx},${cy + dy},${cz + dz}`;
                        const list = grid.get(key);
                        if (!list) continue;

                        for (const j of list) {
                            if (j <= i) continue;
                            if (compFind(j) === compI) continue;
                            const dxv = V[j * 3] - xi;
                            const dyv = V[j * 3 + 1] - yi;
                            const dzv = V[j * 3 + 2] - zi;
                            if (dxv * dxv + dyv * dyv + dzv * dzv <= tolSq) {
                                weldUnion(i, j);
                            }
                        }
                    }
                }
            }
        }

        // 4) Rebuild topology using welded boundary ids.
        const rep = new Int32Array(vertCount);
        for (let i = 0; i < vertCount; i++) rep[i] = isBoundaryVert[i] ? weldFind(i) : i;

        const mergedEdgeUse = new Map<string, number>();
        for (let f = 0; f < faceCount; f++) {
            const i0 = rep[F[f * 3]], i1 = rep[F[f * 3 + 1]], i2 = rep[F[f * 3 + 2]];
            if (i0 === i1 || i1 === i2 || i2 === i0) continue;
            const e0 = edgeKey(i0, i1), e1 = edgeKey(i1, i2), e2 = edgeKey(i2, i0);
            mergedEdgeUse.set(e0, (mergedEdgeUse.get(e0) || 0) + 1);
            mergedEdgeUse.set(e1, (mergedEdgeUse.get(e1) || 0) + 1);
            mergedEdgeUse.set(e2, (mergedEdgeUse.get(e2) || 0) + 1);
        }

        const boundaryEdges: [number, number][] = [];
        mergedEdgeUse.forEach((count, key) => {
            if (count !== 1) return;
            const sep = key.indexOf('_');
            const a = parseInt(key.substring(0, sep), 10);
            const b = parseInt(key.substring(sep + 1), 10);
            boundaryEdges.push([a, b]);
        });
        if (boundaryEdges.length === 0) return [];

        // 5) Undirected boundary graph.
        const adj = new Map<number, number[]>();
        const addAdj = (a: number, b: number): void => {
            let list = adj.get(a);
            if (!list) { list = []; adj.set(a, list); }
            if (list.indexOf(b) === -1) list.push(b);
        };
        for (const [a, b] of boundaryEdges) {
            addAdj(a, b);
            addAdj(b, a);
        }

        // 6) Walk loops; branch vertices choose the straightest continuation.
        const used = new Set<string>();
        const edgeUsed = (a: number, b: number): boolean => used.has(edgeKey(a, b));
        const markEdge = (a: number, b: number): void => { used.add(edgeKey(a, b)); };

        const chooseNext = (prev: number, curr: number, cand: number[]): number => {
            if (cand.length === 1) return cand[0];
            const inX = V[curr * 3] - V[prev * 3];
            const inY = V[curr * 3 + 1] - V[prev * 3 + 1];
            const inZ = V[curr * 3 + 2] - V[prev * 3 + 2];
            const inLen = Math.sqrt(inX * inX + inY * inY + inZ * inZ) || 1;
            const nix = inX / inLen, niy = inY / inLen, niz = inZ / inLen;

            let best = cand[0];
            let bestDot = -Infinity;
            for (const n of cand) {
                const vx = V[n * 3] - V[curr * 3];
                const vy = V[n * 3 + 1] - V[curr * 3 + 1];
                const vz = V[n * 3 + 2] - V[curr * 3 + 2];
                const len = Math.sqrt(vx * vx + vy * vy + vz * vz) || 1;
                const dot = (vx / len) * nix + (vy / len) * niy + (vz / len) * niz;
                if (dot > bestDot) {
                    bestDot = dot;
                    best = n;
                }
            }
            return best;
        };

        const loops: number[][] = [];
        const boundaryEdgeCount = boundaryEdges.length;
        for (const [a, b] of boundaryEdges) {
            if (edgeUsed(a, b)) continue;

            const loopVerts: number[] = [a];
            markEdge(a, b);
            let prev = a;
            let curr = b;
            let closed = false;
            let safety = 0;

            while (safety++ <= boundaryEdgeCount + 5) {
                loopVerts.push(curr);
                if (curr === a) {
                    closed = true;
                    break;
                }

                const neigh = (adj.get(curr) || []).filter(n => n !== prev && !edgeUsed(curr, n));
                if (neigh.length === 0) break;
                const next = chooseNext(prev, curr, neigh);
                markEdge(curr, next);
                prev = curr;
                curr = next;
            }

            if (!closed || loopVerts.length < 4) continue;
            loopVerts.pop();

            const coords: number[] = [];
            for (const vi of loopVerts) {
                coords.push(V[vi * 3], V[vi * 3 + 1], V[vi * 3 + 2]);
            }
            loops.push(coords);
        }

        if (loops.length === 0) return [];

        const loopArea = (coords: number[]): number => {
            const n = coords.length / 3;
            let nx = 0, ny = 0, nz = 0;
            for (let i = 0; i < n; i++) {
                const j = (i + 1) % n;
                const xi = coords[i * 3], yi = coords[i * 3 + 1], zi = coords[i * 3 + 2];
                const xj = coords[j * 3], yj = coords[j * 3 + 1], zj = coords[j * 3 + 2];
                nx += (yi - yj) * (zi + zj);
                ny += (zi - zj) * (xi + xj);
                nz += (xi - xj) * (yi + yj);
            }
            return 0.5 * Math.sqrt(nx * nx + ny * ny + nz * nz);
        };

        const scored = loops
            .map(coords => ({ coords, area: loopArea(coords) }))
            .filter(x => x.area > tol * tol);

        if (scored.length === 0) return [];
        scored.sort((l, r) => r.area - l.area);
        return scored.map(x => x.coords);
    }

    public clearBorder() {
        // Light-model crease edges (LightModelEdges child) are not element borders: keep them,
        // e.g. through LightStructureCollection.setVisibilityForAll -> Section.clearBorders
        // was: for (const child of this.SurfaceDesign.children) {
        // was:     (child as any)?.geometry?.dispose();
        // was: }
        // was: this.SurfaceDesign.children = new Array<Object3D>();
        const kept = new Array<Object3D>();
        for (const child of this.SurfaceDesign.children) {
            if (isLightModelEdges(child)) {
                kept.push(child);
                continue;
            }
            (child as any)?.geometry?.dispose();
        }
        this.SurfaceDesign.children = kept;
    }

    public get area(): number {
        const geometry = this.SurfaceDesign.geometry as any;
        let area = 0;
        if (geometry.index && geometry.index.count > 0)
            for (let i = 0; i < geometry.index.count; i += 3) {
                const vA = new THREE.Vector3(geometry.attributes.position.array[geometry.index.array[i] * 3],
                    geometry.attributes.position.array[geometry.index.array[i] * 3 + 1],
                    geometry.attributes.position.array[geometry.index.array[i] * 3 + 2]);

                const vB = new THREE.Vector3(geometry.attributes.position.array[geometry.index.array[i + 1] * 3],
                    geometry.attributes.position.array[geometry.index.array[i + 1] * 3 + 1],
                    geometry.attributes.position.array[geometry.index.array[i + 1] * 3 + 2]);

                const vC = new THREE.Vector3(geometry.attributes.position.array[geometry.index.array[i + 2] * 3],
                    geometry.attributes.position.array[geometry.index.array[i + 2] * 3 + 1],
                    geometry.attributes.position.array[geometry.index.array[i + 2] * 3 + 2]);

                const tri = new THREE.Triangle(vA, vB, vC);
                area += tri.getArea();
            }
        else
            area = GeometricElement.nonIndexedArea(geometry); // unwelded FEM-linked element
        return area;
    }

    public getCenter(): THREE.Vector3 {
        var center = new THREE.Vector3();
        if (this.SurfaceDesign?.geometry?.boundingBox) {
            this.SurfaceDesign.geometry.boundingBox.getCenter(center);
        } else if (this.SurfaceDesign?.position) {
            center.copy(this.SurfaceDesign.position);
        }
        return center;
    }

    public ComputeAttributes(){
        const boundingBox = new THREE.Box3().setFromObject(this.SurfaceDesign);

        // Step 2: Compute the normal of the mesh
        const geometry = this.SurfaceDesign.geometry as THREE.BufferGeometry;
        geometry.computeVertexNormals();
        const normal = new THREE.Vector3();
        const normalAttribute = geometry.getAttribute('normal');
        normal.fromArray(normalAttribute.array as Float32Array);

        // Step 3: Define a transformation matrix to align the mesh normal with the Z-axis
        let alignmentMatrix = new THREE.Matrix4();
        alignmentMatrix.lookAt(new THREE.Vector3(0, 0, 0), normal, new THREE.Vector3(0, 1, 0));
        const inverseMatrix = alignmentMatrix.clone().invert();

        // Step 4: Transform the bounding box to the plane's local space
        const min = boundingBox.min.clone().applyMatrix4(alignmentMatrix);
        const max = boundingBox.max.clone().applyMatrix4(alignmentMatrix);

        // Step 5: Compute the width and height
        this._Width = Math.abs(max.x - min.x) *1000; 
        this._Height = Math.abs(max.y - min.y) *1000;


        // const geometry = this.SurfaceDesign.geometry as any;
        let area = 0;
        if (geometry.index && geometry.index.count > 0){
            for (let i = 0; i < geometry.index.count; i += 3) {
                const vA = new THREE.Vector3(geometry.attributes.position.array[geometry.index.array[i] * 3]*1000,
                    geometry.attributes.position.array[geometry.index.array[i] * 3 + 1]*1000,
                    geometry.attributes.position.array[geometry.index.array[i] * 3 + 2]*1000);

                const vB = new THREE.Vector3(geometry.attributes.position.array[geometry.index.array[i + 1] * 3]*1000,
                    geometry.attributes.position.array[geometry.index.array[i + 1] * 3 + 1]*1000,
                    geometry.attributes.position.array[geometry.index.array[i + 1] * 3 + 2]*1000);

                const vC = new THREE.Vector3(geometry.attributes.position.array[geometry.index.array[i + 2] * 3]*1000,
                    geometry.attributes.position.array[geometry.index.array[i + 2] * 3 + 1]*1000,
                    geometry.attributes.position.array[geometry.index.array[i + 2] * 3 + 2]*1000);

                const tri = new THREE.Triangle(vA, vB, vC);
                area += tri.getArea();
            }
        }
        else {
            area = GeometricElement.nonIndexedArea(geometry, 1000); // unwelded FEM-linked element
        }

        this._Area = area;
    }
}
